import React, { useRef, useState } from 'react';
import { Plus, Trash2, Upload, X, Loader2 } from 'lucide-react';
import { uploadWebsiteBlockImage } from '../../../../services/storageService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * TestimonialsEditor - Editor component for testimonials block
 */
const TestimonialsEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const items = content.items || [];

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const handleItemChange = (itemId, field, value) => {
    const updatedItems = items.map((item) =>
      item.id === itemId ? { ...item, [field]: value } : item
    );
    handleChange('items', updatedItems);
  };

  const addItem = () => {
    const newItem = {
      id: `testimonial_${Date.now()}`,
      quote: '',
      author: '',
      role: '',
      avatar: '',
    };
    handleChange('items', [...items, newItem]);
  };

  const removeItem = (itemId) => {
    handleChange('items', items.filter((item) => item.id !== itemId));
  };

  return (
    <div className="space-y-4">
      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="What People Say"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Testimonial Items */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Testimonials ({items.length})
        </label>

        <div className="space-y-3">
          {items.map((item, index) => (
            <TestimonialItemEditor
              key={item.id}
              item={item}
              index={index}
              onItemChange={handleItemChange}
              onRemove={removeItem}
              profileId={profileId}
              blockId={block.id}
            />
          ))}
        </div>

        {/* Add Item Button */}
        <button
          onClick={addItem}
          className="mt-3 w-full py-2.5 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center gap-2 text-gray-400 hover:border-[#749555] hover:text-[#749555] transition-colors text-sm"
        >
          <Plus size={18} />
          Add Testimonial
        </button>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
      <SizeSpacingPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['testimonials']}
      />
      <ColorsFontsPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['testimonials']}
        theme={theme || {}}
        blockType="testimonials"
      />
      </>)}
    </div>
  );
};

// Individual testimonial item editor
const TestimonialItemEditor = ({ item, index, onItemChange, onRemove, profileId, blockId }) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const downloadURL = await uploadWebsiteBlockImage(file, profileId, `${blockId}_${item.id}`);
      onItemChange(item.id, 'avatar', downloadURL);
    } catch (error) {
      console.error('Failed to upload avatar:', error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-3">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleAvatarUpload}
            className="hidden"
          />
          {item.avatar ? (
            <div className="relative w-12 h-12">
              <img
                src={item.avatar}
                alt={item.author || 'Avatar'}
                className="w-12 h-12 rounded-full object-cover"
              />
              <button
                onClick={() => onItemChange(item.id, 'avatar', '')}
                className="absolute -top-1 -right-1 p-0.5 bg-black/60 hover:bg-black/80 rounded-full text-white"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-12 h-12 rounded-full border-2 border-dashed border-gray-600 flex items-center justify-center text-gray-400 hover:border-[#749555] hover:text-[#749555] transition-colors"
            >
              {isUploading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Upload size={16} />
              )}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 space-y-2">
          {/* Quote */}
          <textarea
            value={item.quote || ''}
            onChange={(e) => onItemChange(item.id, 'quote', e.target.value)}
            placeholder={`Testimonial ${index + 1}...`}
            rows={2}
            className="w-full px-3 py-2 bg-[#252540] border border-gray-600 rounded text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none text-sm resize-none"
          />

          {/* Author & Role */}
          <div className="flex gap-2">
            <input
              type="text"
              value={item.author || ''}
              onChange={(e) => onItemChange(item.id, 'author', e.target.value)}
              placeholder="Author name"
              className="flex-1 px-3 py-1.5 bg-[#252540] border border-gray-600 rounded text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none text-sm"
            />
            <input
              type="text"
              value={item.role || ''}
              onChange={(e) => onItemChange(item.id, 'role', e.target.value)}
              placeholder="Role (optional)"
              className="flex-1 px-3 py-1.5 bg-[#252540] border border-gray-600 rounded text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none text-sm"
            />
          </div>
        </div>

        {/* Remove Button */}
        <button
          onClick={() => onRemove(item.id)}
          className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-400 transition-colors"
          title="Remove testimonial"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default TestimonialsEditor;
